import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  BrickWall,
  Building2,
  Check,
  ChevronsUpDown,
  KeyRound,
  Layers,
  LogOut,
  Users,
  type LucideIcon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useSession } from "../../session/SessionContext";
import { isOperator } from "../../session/access";
import ChangePasswordModal from "../../fragments/ChangePasswordModal";
import { useCurrentOrganization } from "./useCurrentOrganization";

interface Props {
  className?: string;
  onNavigate(): void;
}

const NavItem: React.FC<{
  to: string;
  icon: LucideIcon;
  label: string;
  active: boolean;
  onNavigate(): void;
}> = ({ to, icon: Icon, label, active, onNavigate }) => (
  <Link
    to={to}
    onClick={onNavigate}
    aria-current={active ? "page" : undefined}
    className={cn(
      "flex h-8 items-center gap-2 rounded-md px-2 text-sm outline-none transition-colors hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring",
      active && "bg-sidebar-accent font-medium text-sidebar-accent-foreground",
    )}
  >
    <Icon className="size-4 shrink-0" />
    <span className="truncate">{label}</span>
  </Link>
);

const OrganizationSwitcher: React.FC<{ operator: boolean; onNavigate(): void }> = ({
  operator,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const { id, name, organizations } = useCurrentOrganization();

  const label = (
    <>
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
        <BrickWall className="size-4" />
      </span>
      <span className="grid min-w-0 flex-1 text-left leading-tight">
        {id && !name ? (
          <Skeleton className="h-4 w-28" />
        ) : (
          <span className="truncate font-semibold">{name ?? "Brixture"}</span>
        )}
        <span className="truncate text-xs text-muted-foreground">
          {id ? "Organisatie" : "Kies een organisatie"}
        </span>
      </span>
    </>
  );

  if (!operator) {
    return <div className="flex items-center gap-2.5 p-2">{label}</div>;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex w-full items-center gap-2.5 rounded-md p-2 text-sm outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-sidebar-accent">
        {label}
        <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Organisaties
        </DropdownMenuLabel>
        {organizations?.map((organization) => (
          <DropdownMenuItem
            key={organization._id}
            onSelect={() => {
              navigate(`/${organization._id}`);
              onNavigate();
            }}
          >
            <span className="flex-1 truncate">{organization.name}</span>
            {organization._id === id && <Check className="text-foreground" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            navigate("/");
            onNavigate();
          }}
        >
          <Building2 />
          Alle organisaties
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const UserMenu: React.FC = () => {
  const { user, signOut } = useSession();
  const navigate = useNavigate();
  const [changingPassword, setChangingPassword] = useState(false);
  const email = user?.email ?? "";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger className="flex w-full items-center gap-2.5 rounded-md p-2 text-sm outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-sidebar-accent">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-accent text-xs font-semibold uppercase">
            {email.slice(0, 2)}
          </span>
          <span className="grid min-w-0 flex-1 text-left leading-tight">
            <span className="truncate font-medium">{email}</span>
            <span className="truncate text-xs text-muted-foreground">
              {isOperator(user) ? "Operator" : "Lid"}
            </span>
          </span>
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="start" className="w-60">
          <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">
            {email}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setChangingPassword(true)}>
            <KeyRound />
            Wachtwoord wijzigen
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              signOut();
              navigate("/login", { replace: true });
            }}
          >
            <LogOut />
            Uitloggen
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ChangePasswordModal open={changingPassword} onClose={() => setChangingPassword(false)} />
    </>
  );
};

const AppSidebar: React.FC<Props> = ({ className, onNavigate }) => {
  const { user } = useSession();
  const operator = isOperator(user);
  const { id } = useCurrentOrganization();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const onMembers = searchParams.get("tab") === "members";

  return (
    <aside
      className={cn("flex-col gap-4 bg-sidebar p-2 text-sidebar-foreground", className)}
      aria-label="Navigatie"
    >
      <OrganizationSwitcher operator={operator} onNavigate={onNavigate} />
      {id && (
        <div className="flex flex-col gap-0.5">
          <div className="px-2 pb-1 text-xs font-medium text-muted-foreground">Beheer</div>
          <NavItem
            to={`/${id}`}
            icon={Layers}
            label="Collecties"
            active={pathname.startsWith(`/${id}`) && !onMembers}
            onNavigate={onNavigate}
          />
          {operator && (
            <NavItem
              to={`/${id}?tab=members`}
              icon={Users}
              label="Leden"
              active={pathname === `/${id}` && onMembers}
              onNavigate={onNavigate}
            />
          )}
        </div>
      )}
      {operator && (
        <div className="flex flex-col gap-0.5">
          <div className="px-2 pb-1 text-xs font-medium text-muted-foreground">Operator</div>
          <NavItem
            to="/"
            icon={Building2}
            label="Alle organisaties"
            active={pathname === "/"}
            onNavigate={onNavigate}
          />
        </div>
      )}
      <div className="mt-auto">
        <UserMenu />
      </div>
    </aside>
  );
};

export default AppSidebar;
